import React, { useCallback, useEffect, useState } from 'react';
import { usePopoverState } from './usePopoverState';
import { useQuerySearch } from './useQuerySearch';
import { useQueryFilters } from './useQueryFilters';
import { useQueryAttachment } from './useQueryAttachment';
import { usePromptSuggestion } from '@/components/PromptInput';
import { useVideoSourceSelected } from '@/hooks';
import { useQueryPanelStore } from '@/stores';
import { extractImageFromClipboard } from '@/utils/clipboard';

interface UseQueryPanelOptions {
    compact?: boolean;
}

export function useQueryPanel(options: UseQueryPanelOptions = {}) {
    const draft = useQueryPanelStore();
    const attachmentState = useQueryAttachment();
    const { isOpen: sourcesListOpen, setIsOpen: setSourcesListOpen } =
        usePopoverState();
    const [sourcesInvalid, setSourcesInvalid] = useState(false);
    const [shakeNonce, setShakeNonce] = useState(0);

    const isVideoSourceSelected = useVideoSourceSelected(
        draft.draftSources.selected,
    );

    const requestSourceSelection = useCallback(() => {
        setSourcesListOpen(true);
        setSourcesInvalid(true);
        setShakeNonce((value) => value + 1);
    }, [setSourcesListOpen]);

    const search = useQuerySearch({
        ...options,
        uploadAttachment: attachmentState.uploadAttachment,
        hasAttachment: !!attachmentState.attachmentFile,
        isUploading: attachmentState.isUploading,
        draft,
        isVideoSourceSelected,
        onMissingSources: requestSourceSelection,
    });
    const filters = useQueryFilters(draft);
    const suggestion = usePromptSuggestion({
        prompt: search.searchQuery,
        onPromptChange: search.setSearchQuery,
        inputRef: search.inputRef,
    });

    const hasSourceSelection =
        draft.draftSources.all || draft.draftSources.selected.size > 0;

    useEffect(() => {
        if (hasSourceSelection && sourcesInvalid) {
            setSourcesInvalid(false);
        }
    }, [hasSourceSelection, sourcesInvalid]);

    const handlePaste = useCallback(
        (e: React.ClipboardEvent<HTMLInputElement | HTMLTextAreaElement>) => {
            if (attachmentState.attachmentFile) return;

            const file = extractImageFromClipboard(e.clipboardData);
            if (file) {
                e.preventDefault();
                void attachmentState.handleAttach({ file });
            }
        },
        [attachmentState.attachmentFile, attachmentState.handleAttach],
    );

    const canSearch =
        !attachmentState.isUploading &&
        !suggestion.isLoadingSuggestion &&
        (!!search.searchQuery.trim() || !!attachmentState.attachmentFile);

    return {
        ...search,
        canSearch,
        handlePaste,
        ...filters,
        ...attachmentState,
        suggestion,
        draft,
        isVideoSourceSelected,
        sourcesListOpen,
        setSourcesListOpen,
        sourcesInvalid,
        shakeNonce,
        requestSourceSelection,
        focusAndExpand: search.focusAndExpand,
    };
}

export type QueryPanelState = ReturnType<typeof useQueryPanel>;
